import { composeEventHandlers } from '@radix-ui/primitive';
import { format, parse } from 'date-fns';
import { useState, useEffect } from 'react';
import { Key } from 'w3c-keys';
import getLocaleData from '../../locale/getLocaleData.mjs';
import { localeValues } from '../components/localeValues.mjs';
import { RELATED_DATE_FORMATS, DATE_FORMAT_RECORD } from '../constants/date-format.mjs';
import { isInvalidDate, onDateChangeWhenChanged, INVALID_DATE } from '../utils/date.mjs';

function useDatePickerField(params) {
  const {
    date,
    dateFormatRecord = DATE_FORMAT_RECORD,
    formats = RELATED_DATE_FORMATS,
    locale,
    onDateChange,
    onBlur,
    onChange,
    onKeyDown,
    onValidate
  } = params;
  const dateFormat = dateFormatRecord[locale];
  const allowedInputValueFormat = formats.concat(dateFormat);
  const localeData = getLocaleData(locale);
  const { invalidDateInputErrorMessage } = localeValues[locale];
  const [dateString, setDateString] = useState(
    () => date !== null && !isInvalidDate(date) ? format(date, dateFormat, { locale: localeData }) : ""
  );
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const clearError = () => {
    setError(false);
    setErrorMessage(void 0);
  };
  const handleChange = composeEventHandlers(onChange, (event) => {
    if (!event.isDefaultPrevented()) {
      const { value } = event.target;
      setDateString(value);
    }
  });
  const handleApplyCurrentInputValue = () => {
    const isEmptyDateString = dateString.trim().length === 0;
    if (isEmptyDateString) {
      clearError();
      onDateChangeWhenChanged({ date, changedDate: null }, onDateChange);
      return;
    }
    const parsedDate = parseDateStringToDate({
      dateString,
      formats: allowedInputValueFormat,
      locale: localeData
    });
    const [valid, validationMessage] = typeof onValidate === "function" ? onValidate(parsedDate) : [true, void 0];
    switch (true) {
      case !valid: {
        setError(true);
        setErrorMessage(validationMessage ?? invalidDateInputErrorMessage);
        break;
      }
      case isInvalidDate(parsedDate): {
        setError(true);
        setErrorMessage(invalidDateInputErrorMessage);
        break;
      }
      default: {
        clearError();
        onDateChangeWhenChanged(
          { date, changedDate: parsedDate },
          onDateChange
        );
      }
    }
  };
  const handleBlur = composeEventHandlers(onBlur, (event) => {
    if (!event.isDefaultPrevented()) {
      handleApplyCurrentInputValue();
    }
  });
  const handleKeyDown = composeEventHandlers(onKeyDown, (event) => {
    if (!event.isDefaultPrevented()) {
      const isEnterKeyDown = event.key === Key.Enter;
      if (isEnterKeyDown) {
        handleApplyCurrentInputValue();
      }
    }
  });
  useEffect(() => {
    if (date === null || isInvalidDate(date)) {
      setDateString("");
      return;
    }
    clearError();
    setDateString(format(date, dateFormat, { locale: localeData }));
  }, [date, dateFormat, localeData]);
  return [
    dateString,
    { onBlur: handleBlur, onChange: handleChange, onKeyDown: handleKeyDown },
    { error, errorMessage },
    handleApplyCurrentInputValue
  ];
}
function parseDateStringToDate(params) {
  const { dateString, formats, locale } = params;
  return formats.reduce(
    (previous, current) => isInvalidDate(previous) ? parse(dateString, current, /* @__PURE__ */ new Date(), { locale }) : previous,
    INVALID_DATE
  );
}

export { useDatePickerField };
//# sourceMappingURL=useDatePickerField.mjs.map
